import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const LABEL = {
  customers: (r) => r.legal_name || r.contact_name || r.id,
  vehicles: (r) => [r.brand, r.model, r.plates].filter(Boolean).join(" ") || r.id,
};

function AssignRow({ kind, record, branches, params, onDone }) {
  const [branchId, setBranchId] = useState("");
  const [saving, setSaving] = useState(false);
  async function assign() {
    setSaving(true);
    try {
      await api.post(`/unassigned-records/${kind}/${record.id}/assign`, { branch_id: branchId }, { params });
      toast.success("Sucursal asignada");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }
  return (
    <TableRow data-testid={`unassigned-row-${record.id}`}>
      <TableCell className="text-sm">{LABEL[kind](record)}</TableCell>
      <TableCell className="text-xs text-muted-foreground">{(record.created_at || "").slice(0, 10)}</TableCell>
      <TableCell>
        <div className="flex gap-2 justify-end">
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger className="w-48 h-8" data-testid={`unassigned-branch-${record.id}`}>
              <SelectValue placeholder="Asignar a…" />
            </SelectTrigger>
            <SelectContent>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" disabled={!branchId || saving} onClick={assign} data-testid={`unassigned-assign-${record.id}`}>
            Asignar
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export default function SinSucursal() {
  const { session } = useAuth();
  const { authorizedBranches: branches, isSuperAdmin, tenantQueryParam } = useTenant();
  const [kind, setKind] = useState("customers");
  const [items, setItems] = useState([]);
  const isOwner = isSuperAdmin || session?.user?.role_name === "dueno";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/unassigned-records/${kind}`, { params: tenantQueryParam });
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [kind, tenantQueryParam]);

  useEffect(() => { if (isOwner) load(); }, [load, isOwner]);

  if (!isOwner) {
    return <div className="text-sm text-muted-foreground" data-testid="unassigned-forbidden">Solo el dueño puede asignar sucursales.</div>;
  }
  return (
    <div className="space-y-6" data-testid="page-sin-sucursal">
      <PageHeader
        title="Sin sucursal asignada"
        description="Clientes y vehículos anteriores a la separación por sucursal. Asígnalos a una de tus sucursales autorizadas."
      />
      <Tabs value={kind} onValueChange={setKind}>
        <TabsList>
          <TabsTrigger value="customers" data-testid="unassigned-tab-customers">Clientes</TabsTrigger>
          <TabsTrigger value="vehicles" data-testid="unassigned-tab-vehicles">Vehículos</TabsTrigger>
        </TabsList>
      </Tabs>
      <Card className="border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{kind === "customers" ? "Cliente" : "Vehículo"}</TableHead>
              <TableHead>Alta</TableHead>
              <TableHead className="text-right">Sucursal</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-sm text-muted-foreground text-center py-8" data-testid="unassigned-empty">
                  No hay registros pendientes.
                </TableCell>
              </TableRow>
            ) : (
              items.map((r) => <AssignRow key={r.id} kind={kind} record={r} branches={branches} params={tenantQueryParam} onDone={load} />)
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
